import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { propertyGroups, propertyGroupEndpoints } from '../api/endpoints.js';
import { propertyGroupRow, type PropertyGroupRow } from '../api/schemas.js';

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const columns: readonly ColumnDescriptor<PropertyGroupRow>[] = [
  {
    id: 'name',
    header: 'Group',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'code'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/property-groups/${row.id}`}
        secondary={row.code ?? null}
      />
    ),
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) => row.status,
  },
  {
    id: 'description',
    header: 'Description',
    fields: ['description'],
    cell: (row) => row.description,
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: PropertyGroupRow): React.JSX.Element => <GroupRowActions row={row} />,
  },
];

function GroupRowActions({ row }: { readonly row: PropertyGroupRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = propertyGroups.useSoftDelete();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/property-groups/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/property-groups/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.PROPERTY_GROUP_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete property group"
        description={`${label} will be retired. Existing references are retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export function PropertyGroupListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Property groups"
      description="Collections of properties used for search and marketing."
      path={propertyGroupEndpoints.list}
      resource="propertyGroups"
      schema={propertyGroupRow}
      columns={columns}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.PROPERTY_GROUP_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.PROPERTY_GROUP_CREATE}>
          <Button asChild>
            <Link to="/property-groups/new">New property group</Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
